import { useState, useRef, useCallback } from "react";
import { useFlowsTemplates, TemplateType } from "../../../../../stores/flowsTemplatesStore";

export interface UseCreateTemplateModalProps {
  onClose: () => void;
}

export interface UseCreateTemplateModalReturn {
  // State values
  name: string;
  templateType: TemplateType;
  selectedFile: File | null;
  loading: boolean;
  dragOver: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;

  // Handlers
  setName: (name: string) => void;
  setTemplateType: (type: TemplateType) => void;
  setSelectedFile: (file: File | null) => void;
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleDrop: (event: React.DragEvent) => void;
  handleDragOver: (event: React.DragEvent) => void;
  handleDragLeave: () => void;
  handleSubmit: () => Promise<void>;
  handleClose: () => void;

  // Utility functions
  formatFileSize: (bytes: number) => string;

  // Derived values
  isSubmitDisabled: boolean;
}

export const TEMPLATE_TYPES: { value: TemplateType; label: string }[] = [
  { value: "monthly_report", label: "Monthly Report" },
  { value: "quarterly_statement", label: "Quarterly Statement" },
  { value: "wire_transfer_summary", label: "Wire Transfer Summary" },
  { value: "account_statement", label: "Account Statement" },
  { value: "client_notes_report", label: "Client Notes Report" },
  { value: "custom_document", label: "Custom Document" },
  { value: "report", label: "Report" },
  { value: "statement", label: "Statement" },
  { value: "confirmation", label: "Confirmation" },
  { value: "tax", label: "Tax Document" },
];

export function useCreateTemplateModal({
  onClose,
}: UseCreateTemplateModalProps): UseCreateTemplateModalReturn {
  // State
  const [name, setName] = useState("");
  const [templateType, setTemplateType] = useState<TemplateType>("custom_document");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Store
  const { createTemplate, uploadTemplateFile } = useFlowsTemplates();

  // Handlers
  const resetForm = useCallback(() => {
    setName("");
    setTemplateType("custom_document");
    setSelectedFile(null);
  }, []);

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        setSelectedFile(file);
        // Auto-fill name from filename if empty
        if (!name) {
          const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
          setName(nameWithoutExt);
        }
      }
    },
    [name]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setDragOver(false);
      const file = event.dataTransfer.files[0];
      if (file) {
        setSelectedFile(file);
        if (!name) {
          const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
          setName(nameWithoutExt);
        }
      }
    },
    [name]
  );

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!name.trim()) return;

    setLoading(true);

    // Create template first
    const templateId = await createTemplate({
      name: name.trim(),
      template_type: templateType,
    });

    // If file selected and template was created, upload the file
    if (selectedFile && templateId) {
      await uploadTemplateFile(templateId, selectedFile);
    }

    setLoading(false);
    resetForm();
    onClose();
  }, [name, templateType, selectedFile, createTemplate, uploadTemplateFile, resetForm, onClose]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  // Utility functions
  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }, []);

  // Derived values
  const isSubmitDisabled = !name.trim() || loading;

  return {
    // State values
    name,
    templateType,
    selectedFile,
    loading,
    dragOver,
    fileInputRef,

    // Handlers
    setName,
    setTemplateType,
    setSelectedFile,
    handleFileSelect,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleSubmit,
    handleClose,

    // Utility functions
    formatFileSize,

    // Derived values
    isSubmitDisabled,
  };
}
